<template>
  <div v-if="!isLoading" class="lesson-forum-topic-resource">
    <div class="header">
      <div class="user-info-section">
        <div v-if="hasAvatar" class="avatar">
          <user-avatar :user-avatar-url="userAvatarUrl" :user-id="createdByUserId" />
        </div>

        <div class="user-name">
          <ellipsis-text :text="userName" />
        </div>
      </div>

      <overflow-menu v-if="canEdit" class="menu-actions" :flip-menu="true">
        <template #trigger>
          <svg-icon name="Kebab24" kind="action" />
        </template>

        <!--        Edit -->
        <OverflowMenuItem @click="handleEdit">
          <svg-icon name="Pencil24" kind="action" />
          {{ t("teacher_reviews_subpage_edit_review_button") }}
        </OverflowMenuItem>

        <!--        Delete -->
        <OverflowMenuItem @click="handleDelete">
          <svg-icon name="Trash24" kind="action" />
          {{ t("teacher_reviews_subpage_delete_review_button") }}
        </OverflowMenuItem>
      </overflow-menu>
    </div>

    <div class="date-time">{{ dateTime }}</div>

    <div class="content">
      <rich-text
        class="description"
        :content="content"
        :expandable="false"
        :expanded="false"
        :truncate="false"
        :truncation-type="truncationType"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import UserAvatarMixin from "@modules/user-avatar/mixin/user.avatar.mixin";
import { NAMESPACE } from "@modules/lesson-review/lesson.topic.constant.ts";
import DateTimeFormatter from "@utils/datetime.formatter";
import appConfigs from "@configs/app";
import { useI18n } from "vue-i18n";
import { computed } from "vue";
import LessonForumTopicResourceEditSubpage from "@modules/lesson-review/components/LessonForumTopicResourceEditSubpage.vue";
import { useModal } from "@plugins/modal/composables/modal-instance.ts";
import LessonForumTopicResourceDeleteModal from "@modules/lesson-review/components/LessonForumTopicResourceDeleteModal.vue";
import {
  ForumTopicModel,
  ForumTopicResourceModel,
} from "@modules/lesson-review/lesson-review-types.ts";
import UserAvatar from "@plugins/common/components/UserAvatar.vue";
import EllipsisText from "@plugins/common/components/EllipsisText.vue";
import OverflowMenu from "@plugins/common/components/OverflowMenu.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import OverflowMenuItem from "@plugins/common/components/OverflowMenuItem.vue";
import RichText from "@plugins/richtext/components/RichText.vue";
import { useContentSubpageInstance } from "@plugins/content-subpage/composables/content-subpage-instance.ts";
import { useVm } from "@utils/vm.ts";
import { useStore } from "vuex";

const { vm } = useVm();

defineOptions({
  mixins: [UserAvatarMixin],
});

// Props typing
interface Props {
  isLoading?: boolean;
  topicResource?: ForumTopicResourceModel;
  titles?: string[];
  truncationType?: string;
  topic: ForumTopicModel;
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  topicResource: () => ({}) as ForumTopicResourceModel,
  titles: () => [],
  truncationType: "secondary",
});

// Emits typing
const emit = defineEmits<{
  editResource: [response: ForumTopicResourceModel];
  deleteResource: [uuid: string];
}>();

// State refs
const { t } = useI18n();
const modal = useModal();
const contentSubpage = useContentSubpageInstance();
const store = useStore();

//Todo Tung: test value
const userId = computed(() => store.getters[`${NAMESPACE.AUTH}/userId`]);

// Computed properties
const userAvatarUrl = computed<string>(() => {
  return hasAvatar.value
    ? vm.getUserAvatarUrlFromFilenameAndVersion(
        props.topicResource.avatarFilename,
        props.topicResource.avatarFileVersion,
      )
    : "";
});

const hasAvatar = computed<boolean>(() => props.topicResource.avatarFilename != null);

const canEdit = computed<boolean>(
  () => userId.value?.toString() === props.topicResource.createdBy?.toString(),
);

const userName = computed<string>(() => props.topicResource.createdByName);

const createdByUserId = computed<number>(() => props.topicResource.createdBy);

const dateTime = computed<string>(() =>
  DateTimeFormatter.formatLocalDateTimeToDateTimeAmPm(props.topicResource.createdDate),
);

const content = computed<string>(() => {
  let displayRemarks = props.topicResource.content;
  displayRemarks = displayRemarks.replace(
    "{{duplicateFromLabel}}",
    t("lesson_duplicate_from_label"),
  );
  displayRemarks = displayRemarks.replace("{{replaceByLabel}}", t("lesson_replace_by"));
  displayRemarks = displayRemarks.replace("{{replaceLessonLabel}}", t("lesson_replaces"));
  displayRemarks = displayRemarks.replace("{{submittedByLabel}}", t("lesson_submitted_by"));
  displayRemarks = displayRemarks.replace(
    "{{viewCommunityResourceUrl}}",
    appConfigs.vleDomain + `/admin/community-gallery/module/view/`,
  );
  return displayRemarks;
});

// Methods
const handleEdit = function () {
  contentSubpage.show(LessonForumTopicResourceEditSubpage, {
    baseTitle: () => (props.titles ? [...props.titles] : []),
    topicResource: props.topicResource,
    topic: props.topic,
    editResourceActionBack: (response: ForumTopicResourceModel) => {
      emit("editResource", response);
    },
  });
};

const handleDelete = function () {
  modal.show(LessonForumTopicResourceDeleteModal, {
    topicResourceUuid: props.topicResource.uuid,
    topicType: props.topic.type,
    actionPrimary: () => {
      emit("deleteResource", props.topicResource.uuid);
    },
  });
};
</script>

<style lang="scss">
@import "@styles/abstracts/mixins";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.lesson-forum-topic-resource {
  margin-bottom: $spacing-05;
  padding: $spacing-05;
  background-color: $ui-01;
  border-radius: 10px;

  .user-info-section {
    display: flex;
    align-items: center;
    gap: $spacing-03;

    .user-name {
      @include carbon--type-style("productive-heading-01");
      span {
        @include line-clamp(1);
      }
    }
  }
}
</style>
